import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CategoryIcon from '@mui/icons-material/Category';
import BarChartIcon from '@mui/icons-material/BarChart';

const FEATURES = [
  {
    icon: AccountBalanceIcon,
    title: 'Extracción automática',
    description:
      'Conecta tu banco y Zort importa tus movimientos en segundo plano. Sin planillas, sin esfuerzo manual.',
  },
  {
    icon: CategoryIcon,
    title: 'Categorización rápida',
    description:
      'Clasifica cada transacción en segundos con nuestra interfaz táctil. Tú decides qué cuenta para qué categoría.',
  },
  {
    icon: BarChartIcon,
    title: 'Dashboard visual',
    description:
      'Visualiza tus gastos por categoría, burn rate y tendencias mensuales. Toma decisiones con datos.',
  },
];

export default function Home() {
  const theme = useTheme();

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* ── Hero ── */}
      <Box
        sx={{
          position: 'relative',
          backgroundColor: 'background.default',
          backgroundImage: `radial-gradient(circle at 50% 0%, ${theme.palette.secondary.light}22, transparent 70%)`,
          py: { xs: 10, md: 14 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="overline"
            sx={{
              display: 'block',
              mb: 2,
              letterSpacing: '0.12em',
            }}
          >
            TUS FINANZAS, BAJO CONTROL
          </Typography>

          <Typography variant="h1" sx={{ mb: 3 }}>
            Controla tus gastos{' '}
            <Box component="span" sx={{ color: 'secondary.main' }}>
              sin esfuerzo
            </Box>
          </Typography>

          <Typography
            variant="h5"
            color="text.secondary"
            sx={{
              maxWidth: 640,
              mx: 'auto',
              mb: 5,
              fontWeight: 400,
              lineHeight: 1.5,
            }}
          >
            Zort importa tus movimientos bancarios automáticamente y te muestra
            dónde va tu dinero, para que tomes mejores decisiones financieras.
          </Typography>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
            alignItems="center"
          >
            <Button
              variant="outlined"
              color="secondary"
              size="large"
              component={Link}
              to="/login"
            >
              Iniciar sesión
            </Button>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              component={Link}
              to="/register"
            >
              Crear cuenta
            </Button>
          </Stack>

        </Container>
      </Box>

      {/* ── Features ── */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="overline" sx={{ display: 'block', mb: 1.5 }}>
            CÓMO FUNCIONA
          </Typography>
          <Typography variant="h3">
            Todo lo que necesitas en un lugar
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {FEATURES.map(({ icon: Icon, title, description }) => (
            <Grid item xs={12} md={4} key={title}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(59, 130, 246, 0.10)',
                      mb: 2.5,
                    }}
                  >
                    <Icon sx={{ color: 'secondary.main', fontSize: 28 }} />
                  </Box>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
                    {title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ── CTA ── */}
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 10 } }}>
        <Paper
          sx={{
            p: { xs: 4, md: 6 },
            textAlign: 'center',
            borderLeft: '4px solid',
            borderLeftColor: 'secondary.main',
            borderRadius: 3,
          }}
        >
          <Typography variant="h3" sx={{ mb: 1.5 }}>
            Comienza a controlar tus finanzas hoy
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 520, mx: 'auto', mb: 4 }}
          >
            Crea tu cuenta, conecta tu banco y deja que Zort haga el resto.
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            component={Link}
            to="/register"
          >
            Crear cuenta gratis
          </Button>
        </Paper>
      </Container>

      {/* ── Footer ── */}
      <Box sx={{ pb: 4, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Zort · Hecho en Chile 🇨🇱
        </Typography>
      </Box>
    </Box>
  );
}
